import { useRef, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import clsx from "clsx";
import { AlertTriangle, CircleCheck, Download, FileSpreadsheet, RotateCcw, Upload } from "lucide-react";
import { api, type S } from "@/shared/api/client";
import i18n, { codeMessage, errorMessage, fieldMessage } from "@/shared/i18n";
import { downloadCsv } from "@/shared/lib/csv";
import { Modal } from "./Modal";
import { MoreMenu, type MoreItem } from "./MoreMenu";

export type ImportKind = S["Import"];
type ImportErr = S["ImportError"];
type Outcome = Omit<S["ImportOutcome"], "sheets" | "columns" | "missing" | "errors"> & {
  sheets: string[]; columns: { header: string; field: string; samples: string[] }[]; missing: string[]; errors: ImportErr[];
};

/** An outcome with its lists, empty ones included. */
function outcome(o: S["ImportOutcome"]): Outcome {
  return { ...o, sheets: o.sheets ?? [], missing: o.missing ?? [], errors: o.errors ?? [], columns: (o.columns ?? []).map((c) => ({ ...c, samples: c.samples ?? [] })) };
}

/** The kinds of record the user may import here, each with its columns. */
export function useImportKinds() {
  return useQuery({ queryKey: ["/v1/core/imports"], queryFn: () => api.get<ImportKind[]>("/v1/core/imports"), staleTime: 5 * 60_000 });
}

/** A label the API gives in both languages, in the one shown. */
export function inLang(x: { label_ar: string; label_en: string }): string {
  return (i18n.language === "en" && x.label_en) || x.label_ar;
}

/** Where the records of each kind are listed, to look at them once imported. */
export const importList: Record<string, string> = {
  customers: "/organizations?type=customers", suppliers: "/organizations?type=suppliers", contacts: "/contacts", products: "/products",
  leads: "/crm/leads", deals: "/crm/deals",
  customer_balances: "/settings/opening-balances", supplier_balances: "/settings/opening-balances", opening_stock: "/inventory",
  employees: "/hr/employees", leave_balances: "/hr/leaves", attendance: "/hr/attendance", projects: "/projects", tasks: "/tasks", units: "/realestate",
  services: "/settings/appointments", appointments: "/appointments/list", accounts: "/accounting/accounts", account_balances: "/accounting/journal", journal_entries: "/accounting/journal", fixed_assets: "/accounting/assets",
};

/** What's wrong with a row, in the user's language: the column, then what. */
export function importErrorText(kind: ImportKind | undefined, e: ImportErr): string {
  const field = kind?.fields?.find((f) => f.key === e.field);
  const what = e.text || (e.code && codeMessage(e.code)) || fieldMessage(e.message ?? "");
  return field ? `${inLang(field)}: ${what}` : what;
}

/** The template's address: an empty workbook of the kind's columns, in the language shown. */
export function templateHref(kind: string, lang = i18n.language) {
  return `/api/v1/core/imports/${kind}/template?lang=${lang === "en" ? "en" : "ar"}`;
}

/**
 * Importing one kind of record, step by step: the template and the file; then the file's
 * columns matched to the fields (guessed from the headers, changed by hand) and every row
 * checked as it would import, nothing saved; then the import, with a way to take it back.
 */
export function ImportWizard({ kind: key, open, onClose }: { kind: string; open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const kinds = useImportKinds();
  const kind = kinds.data?.find((k) => k.key === key);
  const [file, setFile] = useState<File | null>(null);
  const [check, setCheck] = useState<Outcome | null>(null);
  const [done, setDone] = useState<Outcome | null>(null);
  const [busy, setBusy] = useState<"check" | "import" | "undo" | null>(null);
  const [undone, setUndone] = useState<S["UndoResult"] | null>(null);

  // A check or import runs as a job on the server (a big file takes longer than a request
  // may): asked every half second how far it got, until it's done. live says whether its
  // answer is still wanted; once it isn't, the job is no longer asked and send gives null.
  const [progress, setProgress] = useState<Progress>(null);
  const send = async (save: boolean, f: File, opts: { sheet?: string; mapping?: string[]; guess?: boolean }, live: () => boolean): Promise<Outcome | null> => {
    setProgress(null);
    const job = await api.upload<S["ImportJob"]>(`/v1/core/imports/${key}/jobs`,
      { save: save || undefined, sheet: opts.sheet, mapping: opts.mapping?.join(","), guess: opts.guess || undefined }, f);
    for (let wait = 250; ; wait = 500) {
      await new Promise((r) => setTimeout(r, wait));
      if (!live()) return null;
      const j = await api.get<S["ImportJob"]>(`/v1/core/imports/jobs/${job.id}`);
      if (j.finished && j.outcome) return outcome(j.outcome);
      if (live()) setProgress({ done: j.done, total: j.total });
    }
  };

  // The columns as matched, changed at once by the person: a check answers for the
  // latest of them only (one sent before it is left unanswered).
  const [mapping, setMapping] = useState<string[]>([]);
  const latest = useRef(0);
  const runCheck = async (f: File, opts: { sheet?: string; mapping?: string[]; guess?: boolean } = {}) => {
    const n = ++latest.current;
    setBusy("check");
    try {
      const out = await send(false, f, opts, () => n === latest.current);
      if (!out || n !== latest.current) return;
      setCheck(out);
      setMapping(out.columns.map((c) => c.field));
    } catch (e) {
      if (n === latest.current) toast.error(errorMessage(e));
    } finally {
      if (n === latest.current) setBusy(null);
    }
  };
  const pick = (f: File | null) => {
    latest.current++;
    setFile(f);
    setCheck(null);
    setMapping([]);
    setBusy(null);
    if (f) void runCheck(f);
  };
  const remap = (i: number, field: string) => {
    if (!file || !check) return;
    // A field fills one column: choosing it here takes it from the column that had it.
    const next = mapping.map((f, j) => (j === i ? field : f === field && field ? "" : f));
    setMapping(next);
    void runCheck(file, { sheet: check.sheet || undefined, mapping: next });
  };
  const run = async () => {
    if (!file || !check) return;
    setBusy("import");
    const n = ++latest.current;
    try {
      const out = await send(true, file, { sheet: check.sheet || undefined, mapping }, () => n === latest.current);
      if (!out) return;
      setDone(out);
      await qc.invalidateQueries();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };
  const undo = async () => {
    if (!done?.id || !window.confirm(t("imp.undoConfirm", { count: done.ready }))) return;
    setBusy("undo");
    try {
      setUndone(await api.post<S["UndoResult"]>(`/v1/core/imports/log/${done.id}/undo`));
      await qc.invalidateQueries();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };
  const close = () => {
    latest.current++; // a job still running goes on, and shows in the import history
    onClose();
    setFile(null);
    setCheck(null);
    setDone(null);
    setUndone(null);
  };
  const title = kind ? t("imp.titleOf", { what: inLang(kind) }) : t("imp.title");

  let body: ReactNode;
  let footer: ReactNode;
  if (done) {
    const list = importList[key];
    body = <Done kind={kind} out={done} undone={undone} />;
    footer = <>
      {done.saved && kind?.can_undo && !undone && (
        <button type="button" className="btn-ghost me-auto text-danger" disabled={busy !== null} onClick={undo}><RotateCcw className="size-4" /> {t("imp.undo")}</button>
      )}
      <button type="button" className="btn-secondary" onClick={() => { setDone(null); setUndone(null); pick(null); }}>{t("imp.another")}</button>
      {list && done.saved && !undone ? (
        <Link to={list.split("?")[0]} search={Object.fromEntries(new URLSearchParams(list.split("?")[1] ?? "")) as never} className="btn-primary" onClick={close}>{t("imp.openList")}</Link>
      ) : <button type="button" className="btn-primary" onClick={close}>{t("common.close")}</button>}
    </>;
  } else if (file && check) {
    const blocked = check.missing.length > 0;
    body = <Review kind={kind} file={file} check={check} mapping={mapping} busy={busy === "check"} progress={progress} onRemap={remap}
      onSheet={(sheet) => void runCheck(file, { sheet, guess: true })} onChange={() => pick(null)} />;
    footer = <>
      <button type="button" className="btn-secondary" onClick={close}>{t("common.cancel")}</button>
      <button type="button" className="btn-primary" disabled={busy !== null || blocked || check.ready === 0} onClick={run}>
        <Upload className="size-4" /> {busy === "import" ? t("imp.importing", { progress: progress?.total ? t("imp.progress", progress) : "" }) : t("imp.runCount", { count: check.ready })}
      </button>
    </>;
  } else {
    body = <Start kind={kind} kinds={kinds.data ?? []} busy={busy === "check"} progress={progress} file={file} onFile={pick} />;
    footer = <button type="button" className="btn-secondary" onClick={close}>{t("common.close")}</button>;
  }
  return (
    <Modal open={open} onClose={close} title={title} description={kind && !check && !done ? (i18n.language === "en" ? kind.hint_en : kind.hint_ar) : undefined}
      size={check && !done ? "xl" : "lg"} footer={footer}>
      {body}
    </Modal>
  );
}

/** The first step: the template to fill, or any file as it is, and choosing it. */
function Start({ kind, kinds, busy, progress, file, onFile }: {
  kind?: ImportKind; kinds: ImportKind[]; busy: boolean; progress: Progress; file: File | null; onFile: (f: File | null) => void;
}) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const needs = (kind?.needs ?? []).map((n) => kinds.find((k) => k.key === n)).filter((k): k is ImportKind => !!k);
  return (
    <div className="space-y-5">
      <ol className="space-y-5">
        <li className="flex gap-3">
          <Step n={1} />
          <div className="min-w-0 flex-1">
            <p className="font-medium">{t("imp.step1")}</p>
            <p className="mt-0.5 text-[13.5px] text-ink-3">{t("imp.step1Hint")}</p>
            {kind && (
              <div className="mt-2.5 flex flex-wrap items-center gap-2">
                <a className="btn-secondary" href={templateHref(kind.key)} download><Download className="size-4" /> {t("imp.template")}</a>
                <a className="btn-ghost text-[13px]" href={templateHref(kind.key, i18n.language === "en" ? "ar" : "en")} download>{t("imp.templateOther")}</a>
              </div>
            )}
          </div>
        </li>
        <li className="flex gap-3">
          <Step n={2} />
          <div className="min-w-0 flex-1">
            <p className="font-medium">{t("imp.step2")}</p>
            <button type="button" onClick={() => input.current?.click()} disabled={busy}
              onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
              onDrop={(e) => { e.preventDefault(); setOver(false); const f = e.dataTransfer.files?.[0]; if (f) onFile(f); }}
              className={clsx("mt-2.5 flex w-full flex-col items-center gap-1.5 rounded-2xl border-2 border-dashed px-4 py-7 text-center transition-colors",
                over ? "border-ledger bg-ledger-soft" : "border-line hover:border-ink-3/50 hover:bg-paper")}>
              <FileSpreadsheet className="size-7 text-ledger" aria-hidden />
              <span className="font-medium">{busy ? t("imp.checking", { name: file?.name ?? "" }) : t("imp.drop")}</span>
              {busy && progress?.total ? <Bar progress={progress} /> : <span className="text-[13px] text-ink-3">{t("imp.formats")}</span>}
            </button>
            <input ref={input} type="file" className="hidden" accept=".csv,.xlsx,.xls,.txt,.dat,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => { onFile(e.target.files?.[0] ?? null); e.target.value = ""; }} />
          </div>
        </li>
      </ol>
      {needs.length > 0 && (
        <p className="rounded-[var(--radius-ctl)] bg-paper px-3 py-2 text-[13.5px] text-ink-2">
          {t("imp.needs", { what: needs.map(inLang).join(i18n.language === "en" ? ", " : "، ") })}
        </p>
      )}
      <p className="text-[13px] text-ink-3">{t("imp.safe")}</p>
    </div>
  );
}

/** How far a check or import running as a job has got. */
type Progress = { done: number; total: number } | null;

/** A job's rows read so far, as a bar and in words. */
function Bar({ progress }: { progress: NonNullable<Progress> }) {
  const { t } = useTranslation();
  return (
    <span className="block w-full max-w-xs">
      <span className="block h-1.5 overflow-hidden rounded-full bg-line-2" role="progressbar" aria-valuemin={0} aria-valuemax={progress.total} aria-valuenow={progress.done}>
        <span className="block h-full rounded-full bg-ledger transition-[width] duration-300" style={{ width: `${(progress.done / progress.total) * 100}%` }} />
      </span>
      <span className="num mt-1 block text-[12.5px] text-ink-3">{t("imp.progress", progress)}</span>
    </span>
  );
}

function Step({ n }: { n: number }) {
  return <span className="num grid size-7 shrink-0 place-items-center rounded-full bg-ledger-soft text-[13px] font-semibold text-ledger">{n}</span>;
}

/** The file read: what will happen to its rows, its columns matched to fields, and what's wrong. */
function Review({ kind, file, check, mapping, busy, progress, onRemap, onSheet, onChange }: {
  kind?: ImportKind; file: File; check: Outcome; mapping: string[]; busy: boolean; progress: Progress; onRemap: (i: number, field: string) => void; onSheet: (s: string) => void; onChange: () => void;
}) {
  const { t } = useTranslation();
  const fields = kind?.fields ?? [];
  const missing = check.missing.map((m) => fields.find((f) => f.key === m)).filter(Boolean).map((f) => inLang(f!));
  const unused = fields.filter((f) => !mapping.includes(f.key));
  return (
    <div className={clsx("space-y-5 transition-opacity", busy && "opacity-70")} aria-busy={busy}>
      <div className="flex flex-wrap items-center gap-2 text-[13.5px]">
        <FileSpreadsheet className="size-4 text-ledger" aria-hidden />
        <bdi className="font-medium">{file.name}</bdi>
        {check.sheets.length > 1 && (
          <select className="field w-auto py-1 text-[13px]" value={check.sheet} onChange={(e) => onSheet(e.target.value)} aria-label={t("imp.sheet")}>
            {check.sheets.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        <button type="button" className="btn-ghost py-1 text-[13px]" onClick={onChange}>{t("imp.changeFile")}</button>
        {busy && progress?.total ? <span className="ms-auto w-48"><Bar progress={progress} /></span> : null}
      </div>

      {missing.length > 0 ? (
        <div className="flex gap-2.5 rounded-[var(--radius-ctl)] border border-saffron/50 bg-saffron-soft px-3.5 py-3 text-[13.5px]" role="alert">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-[#7a5a1c]" aria-hidden />
          <p>{t("imp.missing", { fields: missing.join(i18n.language === "en" ? ", " : "، ") })}</p>
        </div>
      ) : (
        <div className="grid gap-2 sm:grid-cols-3">
          <Tile tone="green" n={check.ready} label={t("imp.ready")} />
          <Tile tone="neutral" n={check.skipped} label={t("imp.skipped")} />
          <Tile tone="red" n={check.failed} label={t("imp.failed")} />
        </div>
      )}

      <section aria-labelledby="imp-columns">
        <h3 id="imp-columns" className="mb-1 font-semibold">{t("imp.columns")}</h3>
        <p className="mb-2.5 text-[13px] text-ink-3">{t("imp.columnsHint", { rows: check.rows })}</p>
        <div className="divide-y divide-line-2 rounded-[var(--radius-ctl)] border border-line">
          {check.columns.map((c, i) => (
            <div key={i} className="grid items-center gap-x-4 gap-y-1.5 px-3 py-2.5 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1fr)]">
              <span className="truncate font-medium" title={c.header}>{c.header || <span className="text-ink-3">{t("imp.noHeader")}</span>}</span>
              <span className="truncate text-[13px] text-ink-3" title={c.samples.join(" · ")}>{c.samples.join(" · ") || "—"}</span>
              <select className={clsx("field py-1.5 text-[13.5px]", !mapping[i] && "text-ink-3")} value={mapping[i] ?? ""} onChange={(e) => onRemap(i, e.target.value)}
                aria-label={t("imp.fieldFor", { header: c.header })}>
                <option value="">{t("imp.skipColumn")}</option>
                {fields.map((f) => <option key={f.key} value={f.key}>{inLang(f)}{f.required ? " *" : ""}</option>)}
              </select>
            </div>
          ))}
        </div>
        {unused.length > 0 && (
          <p className="mt-2 text-[13px] text-ink-3">{t("imp.unused", { fields: unused.map(inLang).join(i18n.language === "en" ? ", " : "، ") })}</p>
        )}
      </section>

      {check.errors.length > 0 && <Problems kind={kind} out={check} />}
    </div>
  );
}

function Tile({ tone, n, label }: { tone: "green" | "neutral" | "red"; n: number; label: string }) {
  return (
    <div className={clsx("rounded-[var(--radius-ctl)] border px-3.5 py-2.5", {
      green: "border-ledger/30 bg-ledger-soft", neutral: "border-line bg-paper", red: n ? "border-danger/30 bg-danger-soft" : "border-line bg-paper" }[tone])}>
      <span className={clsx("num block text-[22px] leading-tight font-bold", { green: "text-ledger-dark", neutral: "text-ink-2", red: n ? "text-danger" : "text-ink-3" }[tone])}>{n}</span>
      <span className="text-[13px] text-ink-2">{label}</span>
    </div>
  );
}

/** The rows with something wrong, each with what, and the list to download beside the file. */
function Problems({ kind, out }: { kind?: ImportKind; out: Outcome }) {
  const { t } = useTranslation();
  const download = () => downloadCsv(`${kind?.key ?? "import"}-problems.csv`, [
    [t("imp.row"), t("imp.problem")],
    ...out.errors.map((e) => [String(e.row), importErrorText(kind, e)]),
  ]);
  return (
    <section aria-labelledby="imp-problems">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h3 id="imp-problems" className="font-semibold">{t("imp.problems")}</h3>
        <button type="button" className="btn-ghost ms-auto py-1 text-[13px]" onClick={download}><Download className="size-4" /> {t("imp.downloadProblems")}</button>
      </div>
      <p className="mb-2 text-[13px] text-ink-3">{t("imp.problemsHint")}</p>
      <ul className="max-h-56 divide-y divide-line-2 overflow-y-auto rounded-[var(--radius-ctl)] border border-line text-[13.5px]">
        {out.errors.map((e, i) => (
          <li key={i} className="flex gap-3 px-3 py-2">
            <span className="num w-14 shrink-0 text-ink-3">{t("imp.row")} {e.row}</span>
            <span className="text-danger">{importErrorText(kind, e)}</span>
          </li>
        ))}
      </ul>
      {out.failed > out.errors.length && <p className="mt-1.5 text-[13px] text-ink-3">{t("imp.moreProblems", { count: out.failed - out.errors.length })}</p>}
    </section>
  );
}

/** What the import did, and what was taken back. */
function Done({ kind, out, undone }: { kind?: ImportKind; out: Outcome; undone: S["UndoResult"] | null }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3">
        {undone ? <RotateCcw className="mt-0.5 size-6 shrink-0 text-ink-2" aria-hidden /> : <CircleCheck className="mt-0.5 size-6 shrink-0 text-ledger" aria-hidden />}
        <div>
          <p className="text-[17px] font-semibold">
            {undone ? t("imp.undone", { count: undone.removed }) : out.saved ? t("imp.done", { count: out.ready }) : t("imp.nothing")}
          </p>
          <p className="mt-1 text-ink-2">
            {undone ? (undone.kept > 0 ? t("imp.kept", { count: undone.kept }) : null)
              : [out.skipped > 0 && t("imp.doneSkipped", { count: out.skipped }), out.failed > 0 && t("imp.doneFailed", { count: out.failed })].filter(Boolean).join(" ")}
          </p>
        </div>
      </div>
      {undone && (undone.errors ?? []).length > 0 && (
        <ul className="space-y-1 text-[13.5px] text-ink-2">{(undone.errors ?? []).map((e, i) => <li key={i}>{importErrorText(kind, e)}</li>)}</ul>
      )}
      {!undone && out.errors.length > 0 && <Problems kind={kind} out={out} />}
    </div>
  );
}

/** A button that opens the import of one kind (an empty list's first step), when the user may import it. */
export function ImportButton({ kind, label }: { kind: string; label?: string }) {
  const { t } = useTranslation();
  const kinds = useImportKinds();
  const [open, setOpen] = useState(false);
  if (!kinds.data?.some((k) => k.key === kind)) return null;
  return (
    <>
      <button type="button" className="btn-secondary" onClick={() => setOpen(true)}>
        <Upload className="size-4" /> {label ?? t("imp.import")}
      </button>
      <ImportWizard kind={kind} open={open} onClose={() => setOpen(false)} />
    </>
  );
}

/**
 * A page's "More" menu with its imports (the journal's: entries and opening balances), those
 * the user may do here, after the page's own items.
 */
export function ImportMore({ kinds: keys, items = [] }: { kinds: string[]; items?: (MoreItem | false | null | undefined)[] }) {
  const { t } = useTranslation();
  const kinds = useImportKinds();
  const [open, setOpen] = useState<string>();
  const mine = (kinds.data ?? []).filter((k) => keys.includes(k.key));
  if (mine.length === 0 && !items.some(Boolean)) return null;
  return (
    <>
      <MoreMenu items={[...items, ...mine.map((k) => ({ label: t("imp.titleOf", { what: inLang(k) }), icon: <Upload className="size-4" />, onClick: () => setOpen(k.key) }))]} />
      {open && <ImportWizard kind={open} open onClose={() => setOpen(undefined)} />}
    </>
  );
}

/**
 * A list's less-used actions behind one "More" button: the page's own (`items`), then
 * downloading the list (GET {path}/export) and, with `importKind`, importing a spreadsheet.
 */
export function ListMore({ path, query, importKind, items = [] }: { path: string; query?: Record<string, string>; importKind?: string | false; items?: (MoreItem | false | null | undefined)[] }) {
  const { t } = useTranslation();
  const [importing, setImporting] = useState(false);
  const kinds = useImportKinds();
  const may = !!importKind && !!kinds.data?.some((k) => k.key === importKind);
  const qs = query ? "?" + new URLSearchParams(Object.entries(query).filter(([, v]) => v)).toString() : "";
  return (
    <>
      <MoreMenu items={[
        ...items,
        { label: t("imp.export"), icon: <Download className="size-4" />, href: `/api${path}/export${qs === "?" ? "" : qs}`, download: true },
        may ? { label: t("imp.import"), icon: <Upload className="size-4" />, onClick: () => setImporting(true) } : null,
      ]} />
      {may && importKind ? <ImportWizard kind={importKind} open={importing} onClose={() => setImporting(false)} /> : null}
    </>
  );
}
